import React, { useContext } from "react";
import {
  getEdemaSummary,
  getLabel,
  getLabelTag,
  getLabelTagInput,
  getLabelTagYesNO,
  getLabelTagsWithRadioOptions,
} from "../../InspectionSummaries/constructTextFromValues";
import {
  consiousnessOptions,
  cyanosisOptions,
  edemaOptions,
  hydrationOptions,
  moreConsiousnessOptions,
  nmmsOptions,
  pupilOptions,
  pupilRadioOptions,
  pupilSideOptions,
} from "../../Options/genExamOptions";
import examinationIcon from "../../../../assets/doctorIcons/examination.svg";

import { DailyReviewContext } from "../../Doctor/DailyReview/DailyReviewFormProvider";
import { Image, Space } from "antd";
import SummaryWrapper from "../../InspectionSummaries/SummaryWrapper";
import { FileSearchOutlined } from "@ant-design/icons";

const formatGcsEvm = (gcs) => {
  const part = (prefix, value) => {
    if (value === null || value === undefined) return null;
    if (value === "" || value === 0) return null;
    return `${prefix}${value}`;
  };

  const parts = [
    part("E", gcs?.eye_opening_response),
    part("V", gcs?.verbal_response),
    part("M", gcs?.motor_response),
  ].filter(Boolean);

  return parts.length ? parts.join(" ") : null;
};

const DrGeneralExaminationSummaryDR = () => {
  const { formData } = useContext(DailyReviewContext);
  const constructGeneralExaminatinSummaryDRItems = () => {
    const generalExaminationSummaryDRItems = [];

    if (formData?.general_examination?.level_of_consciousness) {
      generalExaminationSummaryDRItems.push({
        key: "1",
        label: "Level of Consciousness",
        children: (
          <>
            {getLabelTag(
              consiousnessOptions,
              formData?.general_examination?.level_of_consciousness
            )}
            {formData?.general_examination?.level_of_consciousness === "p"
              ? getLabelTag(
                  moreConsiousnessOptions,
                  formData?.general_examination?.level_of_consciousness_options
                )
              : ""}
          </>
        ),
      });
    }
    if (
      formData?.general_examination?.gcs?.eye_opening_response ||
      formData?.general_examination?.gcs?.verbal_response ||
      formData?.general_examination?.gcs?.motor_response
    ) {
      const evm = formatGcsEvm(formData?.general_examination?.gcs);
      generalExaminationSummaryDRItems.push({
        key: "2",
        label: "Glasgow Coma Scale",
        children: (
          <>
            {evm ? getLabel(evm) : null}
          </>
        ),
      });
    }
    if (formData?.general_examination?.pupil_condition) {
      generalExaminationSummaryDRItems.push({
        key: "3",
        label: "Pupils",
        children: (
          <>
            {getLabelTag(
              pupilOptions,
              formData?.general_examination?.pupil_condition
            )}
            {getLabelTagsWithRadioOptions(
              pupilSideOptions,
              pupilRadioOptions,
              formData?.general_examination
            )}
          </>
        ),
      });
    }
    if (formData?.general_examination?.hydration) {
      generalExaminationSummaryDRItems.push({
        key: "4",
        label: "Hydration",
        children: getLabelTag(
          hydrationOptions,
          formData?.general_examination?.hydration
        ),
      });
    }
    if (formData?.general_examination?.pallor) {
      generalExaminationSummaryDRItems.push({
        key: "5",
        label: "Pallor",
        children: getLabelTag(
          nmmsOptions,
          formData?.general_examination?.pallor
        ),
      });
    }
    if (formData?.general_examination?.icterus) {
      generalExaminationSummaryDRItems.push({
        key: "6",
        label: "Icterus",
        children: getLabelTag(
          nmmsOptions,
          formData?.general_examination?.icterus
        ),
      });
    }
    if (formData?.general_examination?.cyanosis) {
      generalExaminationSummaryDRItems.push({
        key: "7",
        label: "Cyanosis",
        children: getLabelTag(
          cyanosisOptions,
          formData?.general_examination?.cyanosis
        ),
      });
    }
    if (getEdemaSummary(edemaOptions, formData?.general_examination)) {
      generalExaminationSummaryDRItems.push({
        key: "8",
        label: "Edema",
        children: getEdemaSummary(edemaOptions, formData?.general_examination),
      });
    }
    if (
      formData?.general_examination?.temperature ||
      formData?.general_examination?.pulse_rate ||
      formData?.general_examination?.respiratory_rate ||
      formData?.general_examination?.systolic_bp ||
      formData?.general_examination?.diastolic_bp ||
      formData?.general_examination?.spo2
    ) {
      generalExaminationSummaryDRItems.push({
        key: "9",
        label: "Vitals at the Time of Admission",
        children: (
          <>
            {getLabelTagInput(
              "Temperature",
              formData?.general_examination?.temperature,
              "°F"
            )}
            {getLabelTagInput(
              "Pulse Rate",
              formData?.general_examination?.pulse_rate,
              "beats/min"
            )}
            {getLabelTagInput(
              "Respiratory Rate",
              formData?.general_examination?.respiratory_rate,
              "breaths/min"
            )}
            {getLabelTagInput(
              "Systolic BP",
              formData?.general_examination?.systolic_bp,
              "mmHg"
            )}
            {getLabelTagInput(
              "Diastolic BP",
              formData?.general_examination?.diastolic_bp,
              "mmHg"
            )}
            {getLabelTagInput("spO2", formData?.general_examination?.spo2, "%")}
          </>
        ),
      });
    }
    if (
      formData?.general_examination?.pain_scale ||
      formData?.general_examination?.pain
    ) {
      generalExaminationSummaryDRItems.push({
        key: "10",
        label: "Pain Scale",
        children: (
          <>
            {formData?.general_examination?.pain_scale != null &&
              getLabelTagYesNO(formData?.general_examination?.pain_scale)}
            {getLabelTagInput(
              "Pain level",
              formData?.general_examination?.pain
            )}
            {getLabelTagInput(
              "Interpretation",
              formData?.general_examination?.pain_interpretation
            )}
            {getLabelTagInput(
              "Remarks",
              formData?.general_examination?.pain_scale_comments
            )}
          </>
        ),
      });
    }
    return generalExaminationSummaryDRItems;
  };

  const generalExaminationSummaryDRItems =
    constructGeneralExaminatinSummaryDRItems();

  return (
    <div>
      {generalExaminationSummaryDRItems?.length > 0 && (
        <SummaryWrapper
          items={generalExaminationSummaryDRItems}
          title={
            <span>
              <FileSearchOutlined style={{ color: "var(--ant-primary-color)" }} />
              <span
                style={{
                  fontSize: "16px",
                  fontWeight: "normal",
                  marginLeft: "5px",
                }}
              >
                General Examination
              </span>
            </span>
          }
        />
      )}
    </div>
  );
};

export default DrGeneralExaminationSummaryDR;
